import { ResourcefulRouterVerb } from '../types'
import { joi } from '@nhtio/lucid-resourceful/joi'
import { isObject, isResourcefulModel } from '../../type_guards'
import {
  E_INVALID_RESOURCEFUL_MODELS_MAP,
  E_UNRESOLVABLE_MODEL,
} from '@nhtio/lucid-resourceful/errors'
import type { ValidationError } from '@nhtio/lucid-resourceful/joi'
import type { ResourcefulModel } from '@nhtio/lucid-resourceful/types'
import type { MacroModelsMap, Resolvable, ResourcefulRouterModelMappedOptions } from '../types'

const MacroModelsMapSchema = joi.object<MacroModelsMap>().pattern(
  joi.string().min(1).required(),
  joi
    .object<ResourcefulRouterModelMappedOptions>({
      model: joi.alternatives(
        joi.function().class(),
        joi
          .object()
          .instance(Promise)
          .custom((value, helpers) => {
            if (
              typeof value.then !== 'function' ||
              value.then.length === 0 ||
              typeof value.catch !== 'function' ||
              value.catch.length === 0
            ) {
              return helpers.error('any.invalid')
            }
            return value
          }, 'Promise validation'),
        joi
          .function()
          .arity(0)
          .custom((value, helpers) => {
            try {
              const result = value()
              if (
                typeof result === 'object' &&
                result !== null &&
                typeof (result as Promise<unknown>).then === 'function' &&
                (result as Promise<unknown>).then.length > 0 &&
                typeof (result as Promise<unknown>).catch === 'function' &&
                (result as Promise<unknown>).catch.length > 0
              ) {
                return result
              } else if (typeof result === 'function') {
                return helpers.error('any.invalid')
              } else if (typeof result === 'object' && result !== null && 'default' in result) {
                return result.default
              } else {
                return result
              }
            } catch {
              return helpers.error('any.invalid')
            }
          }, 'Lazy import validation')
      ),
      crudActionMiddlewares: joi
        .object(
          Object.fromEntries([...ResourcefulRouterVerb].map((verb) => [verb, joi.any().optional()]))
        )
        .optional(),
    })
    .unknown(true)
)

export class ResourcefulRouterModelResolverService {
  readonly #cache: Map<string, Promise<ResourcefulModel | E_UNRESOLVABLE_MODEL>>
  readonly #errors: Map<string, E_UNRESOLVABLE_MODEL>
  readonly #crudActionMiddlewaresByModel: Map<
    string,
    MacroModelsMap[string]['crudActionMiddlewares']
  >
  private constructor(map: MacroModelsMap) {
    this.#cache = new Map<string, Promise<ResourcefulModel | E_UNRESOLVABLE_MODEL>>()
    this.#errors = new Map<string, E_UNRESOLVABLE_MODEL>()
    this.#crudActionMiddlewaresByModel = new Map<
      string,
      MacroModelsMap[string]['crudActionMiddlewares']
    >()
    Object.entries(map).forEach(([name, { model: resolveable, crudActionMiddlewares }]) => {
      this.#cache.set(name, this.#resolveToPromise(name, resolveable))
      this.#crudActionMiddlewaresByModel.set(name, crudActionMiddlewares)
      Object.defineProperty(this, name, {
        get: () => this.#errors.get(name) ?? this.#cache.get(name),
        enumerable: true,
        configurable: false,
      })
    })
  }

  // @ts-ignore
  public getCrudActionMiddlewaresForModel(
    modelName: string
  ): MacroModelsMap[string]['crudActionMiddlewares'] | undefined {
    return this.#crudActionMiddlewaresByModel.get(modelName)
  }

  async #resolveToPromise(
    name: string,
    resolveable: Resolvable<ResourcefulModel>
  ): Promise<ResourcefulModel | E_UNRESOLVABLE_MODEL> {
    if (isResourcefulModel(resolveable)) {
      return resolveable
    }
    let resolved: unknown
    try {
      if (typeof resolveable === 'function') {
        resolved = await Promise.resolve(resolveable())
      } else {
        resolved = await Promise.resolve(resolveable)
      }
    } catch (err) {
      const e = new E_UNRESOLVABLE_MODEL(name, err)
      this.#errors.set(name, e)
      return e
    }
    if (isResourcefulModel(resolved)) {
      return resolved
    }
    if (isObject(resolved) && 'default' in resolved && isResourcefulModel(resolved.default)) {
      return resolved.default
    }
    const e = new E_UNRESOLVABLE_MODEL(name)
    this.#errors.set(name, e)
    return e
  }

  [key: string]: Promise<ResourcefulModel> | E_UNRESOLVABLE_MODEL | undefined

  public static init(map: unknown): ResourcefulRouterModelResolverService {
    const { error, value } = MacroModelsMapSchema.validate(map, {
      abortEarly: true,
      convert: true,
      allowUnknown: false,
      stripUnknown: true,
    }) as { error?: ValidationError; value: MacroModelsMap }
    if (error) {
      throw new E_INVALID_RESOURCEFUL_MODELS_MAP(error)
    }
    return new ResourcefulRouterModelResolverService(value)
  }
}
